import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Send, Loader2, Sparkles, Bot, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { buildDeterministicAnswer } from "@/lib/patientLabSearch";

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};

export default function ChatIA() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const basePath: "/app" | "/paciente" = location.pathname.startsWith("/paciente")
    ? "/paciente"
    : "/app";
  const accessMode = basePath === "/paciente" ? "patient" : "professional";

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Inicializar com a primeira pergunta e resposta
  useEffect(() => {
    const { initialQuery, initialAnswer } = location.state || {};

    if (initialQuery && initialAnswer) {
      setMessages([
        {
          id: '1',
          role: 'user',
          content: initialQuery,
          timestamp: new Date()
        },
        {
          id: '2',
          role: 'assistant',
          content: initialAnswer,
          timestamp: new Date()
        }
      ]);
    } else {
      // Redirecionar se não houver contexto inicial
      navigate(basePath);
      toast.error("Inicie uma conversa através da busca inteligente");
    }
  }, [location.state, navigate, basePath]);

  // Auto-scroll para última mensagem
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      if (basePath === "/paciente") {
        const direct = await buildDeterministicAnswer({
          userId: user.id,
          query: inputValue,
        });
        if (direct.handled) {
          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: direct.answer,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, assistantMessage]);
          return;
        }
      }

      // Construir histórico completo da conversa
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const { data, error } = await supabase.functions.invoke("ai-intelligent-search", {
        body: {
          query: inputValue,
          conversationHistory: conversationHistory,
          scope: "all",
          userId: user.id,
          accessMode,
        },
      });

      if (error) throw error;

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.answer || "Desculpe, não consegui processar sua pergunta.",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error("Erro ao enviar mensagem:", error);
      toast.error("Erro ao processar sua mensagem");

      // Remover última mensagem do usuário em caso de erro
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col animate-fade-in">
      {/* Header */}
      <Card className="border-b rounded-none shadow-md bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="flex items-center gap-4 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(basePath)}
            className="hover:bg-primary/10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          <div className="flex items-center gap-3 flex-1">
            <div className="p-2 rounded-lg bg-primary/20 animate-pulse">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-primary">Conversa com IA</h1>
              <p className="text-sm text-muted-foreground">
                Continue fazendo perguntas sobre seus dados
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4 md:p-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex gap-3 animate-scale-in",
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {message.role === 'assistant' && (
                <div className="p-2 rounded-full bg-primary/20 h-10 w-10 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-5 w-5 text-primary" />
                </div>
              )}

              <Card
                className={cn(
                  "max-w-[80%] p-4 shadow-lg",
                  message.role === 'user'
                    ? 'bg-gradient-to-br from-primary to-primary/90 text-white border-primary'
                    : 'bg-gradient-to-br from-background to-secondary/5 border-primary/20'
                )}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {message.content}
                </p>
                <p className={cn(
                  "text-xs mt-2",
                  message.role === 'user' ? 'text-white/70' : 'text-muted-foreground'
                )}>
                  {message.timestamp.toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </Card>

              {message.role === 'user' && (
                <div className="p-2 rounded-full bg-secondary/20 h-10 w-10 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-secondary" />
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3 animate-scale-in">
              <div className="p-2 rounded-full bg-primary/20 h-10 w-10 flex items-center justify-center">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <Card className="max-w-[80%] p-4 bg-gradient-to-br from-background to-secondary/5 border-primary/20">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Pensando...</p>
                </div>
              </Card>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <Card className="border-t rounded-none shadow-lg bg-background/95 backdrop-blur">
        <div className="p-4">
          <div className="max-w-4xl mx-auto flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Digite sua próxima pergunta..."
              disabled={isLoading}
              className="flex-1 h-12 bg-background/50 border-primary/20 focus:border-primary shadow-sm"
            />
            <Button
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              size="lg"
              className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-all shadow-lg hover:shadow-xl px-6"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
